import { ArrowBack } from "@mui/icons-material";
import { ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { LogoHorizontalSVG } from "../../../assets/svg/SvgIcons";

type AuthLayoutProps = {
  title?: string;
  subtitle?: ReactNode;
  children: ReactNode;
  /** Rendered under the form, above the back link (e.g. "Already have an account?"). */
  footer?: ReactNode;
  /** Wider card for the longer registration form. */
  wide?: boolean;
};

/**
 * Shared shell for every auth screen. Uses the landing page's design tokens
 * so signing in looks like the same product as the marketing page — a flat
 * surface and one raised card, with no gradient backdrops.
 */
const AuthLayout = ({
  title,
  subtitle,
  children,
  footer,
  wide,
}: AuthLayoutProps) => {
  return (
    <Wrapper>
      <div className={`auth-card${wide ? " is-wide" : ""}`}>
        <Link
          to="/"
          className="auth-brand"
          aria-label="Go to Smart Parking home"
        >
          <img src={LogoHorizontalSVG} width={150} alt="Smart Parking" />
        </Link>

        {title ? (
          <div className="auth-head">
            <h1>{title}</h1>
            {subtitle ? <p>{subtitle}</p> : null}
          </div>
        ) : null}

        <div className="auth-body">{children}</div>

        {footer ? <div className="auth-footer">{footer}</div> : null}

        <Link to="/" className="auth-back">
          <ArrowBack sx={{ fontSize: 16 }} /> Back to home
        </Link>
      </div>
    </Wrapper>
  );
};

export default AuthLayout;

const Wrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: var(--lp-bg-alt);
  font-family: var(--landing-font-body);
  color: var(--lp-text);

  .auth-card {
    width: 100%;
    max-width: 440px;
    padding: 40px;
    border-radius: var(--lp-r-xl);
    border: 1px solid var(--lp-border);
    background: var(--lp-raised);
    box-shadow: var(--lp-shadow-lg);
    display: flex;
    flex-direction: column;
    gap: 22px;
  }

  .auth-card.is-wide {
    max-width: 620px;
  }

  .auth-brand {
    display: inline-flex;
    align-items: center;
  }

  .auth-head {
    display: grid;
    gap: 6px;

    h1 {
      margin: 0;
      font-family: var(--landing-font-heading);
      font-size: 1.6rem;
      font-weight: 700;
      letter-spacing: -0.02em;
      color: var(--lp-text);
    }

    p {
      margin: 0;
      font-size: 0.9rem;
      line-height: 1.55;
      color: var(--lp-muted);
    }
  }

  .auth-body {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  /* antd's primary button, matched to the landing page's pill CTA.
     antd v5 injects its own styles at runtime and wins on cascade order, so
     the radius needs !important. The colour is scoped to :not(:disabled) so a
     disabled submit still reads as disabled. */
  .auth-body .ant-btn-primary,
  .auth-footer .ant-btn-primary {
    height: 46px;
    border-radius: var(--lp-r-pill) !important;
    font-weight: 600;
  }

  .auth-body .ant-btn-primary:not(:disabled),
  .auth-footer .ant-btn-primary:not(:disabled) {
    background: var(--lp-primary);

    &:hover {
      background: var(--lp-primary-hover) !important;
    }
  }

  /* ForgotPassword and ResetPassword are built on CoreUI, so their submit is
     a Bootstrap .btn rather than an antd one — matched here to the same pill.
     .btn-theme in _custom.scss forces the app's older #3a92c9 blue with
     !important, so overriding it needs !important too. Scoped to the auth
     card so the rest of the app's buttons are untouched. */
  .auth-body .btn-primary,
  .auth-body .btn-theme {
    height: 46px;
    padding-inline: 26px;
    border-radius: var(--lp-r-pill);
    border-color: var(--lp-primary) !important;
    background-color: var(--lp-primary) !important;
    color: #fff;
    font-weight: 600;

    &:hover {
      border-color: var(--lp-primary-hover) !important;
      background-color: var(--lp-primary-hover) !important;
    }
  }

  .auth-body a {
    color: var(--lp-primary);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .auth-footer {
    font-size: 0.88rem;
    color: var(--lp-muted);
    text-align: center;
  }

  .auth-back {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--lp-border);
    margin-top: 2px;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--lp-muted);
    text-decoration: none;

    &:hover {
      color: var(--lp-primary);
    }
  }

  @media (max-width: 560px) {
    padding: 24px 16px;

    .auth-card {
      padding: 28px 22px;
    }
  }
`;
